import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format } from "date-fns";
import {
  fetchDeposits,
  collectDepositPayment,
} from "../../store/thunks/paymentsThunk";
import { ToastContainer, toast } from "react-toastify";

export default function ClientDeposits() {
  const dispatch = useDispatch();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    paymentMethod: "credit",
    paymentAmount: "",
    paymentDate: new Date().toISOString().split("T")[0],
  });

  const client = useSelector((state) => state.clients.selectedClient);
  const user = useSelector((state) => state.auth.user);
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);
  const paymentMethods =
    selectedClinic?.paymentMethods?.map((method) => method.value) || [];

  const loadDeposits = async () => {
    if (!client?._id) return;
    try {
      const response = await dispatch(fetchDeposits(client._id)).unwrap();
      setDeposits(Array.isArray(response.deposits) ? response.deposits : []);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch deposits");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeposits();
  }, [client, dispatch]);

  const handleCollectDeposit = async () => {
    try {
      const response = await dispatch(
        collectDepositPayment({
          paymentMethod: paymentDetails.paymentMethod,
          paymentAmount: Number(paymentDetails.paymentAmount),
          paymentDate: paymentDetails.paymentDate,
          createdBy: user._id,
          clientId: client._id,
        })
      ).unwrap();

      if (response.payment) {
        setShowCollectModal(false);
        await loadDeposits(); // Reload deposits after successful collection
        toast.success(response.message || "Deposit collected successfully");
      }
    } catch (error) {
      console.error("Error collecting deposit:", error);
      toast.error("Failed to collect deposit. Please try again.");
    }
  };

  if (loading) {
    return <div className="p-6">Loading deposits...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 space-y-6">
      <ToastContainer />
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Client Deposits</h2>
        <button
          onClick={() => setShowCollectModal(true)}
          className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-9 px-4 py-2 shadow-sm">
          Collect Deposit
        </button>
      </div>

      {/* Deposits List */}
      {!Array.isArray(deposits) || deposits.length === 0 ? (
        <div className="text-sm text-gray-500">No deposits found for this client.</div>
      ) : (
        <div className="space-y-4">
          {deposits.map((deposit) => (
            <div
              key={deposit?._id || Math.random()}
              className="bg-white rounded-sm shadow-sm border border-gray-100 p-4 space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Deposit Amount:</span>
                  <span className="text-sm font-medium text-green-600">
                    ${deposit?.amount?.toFixed(2) || "0.00"}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Current Balance:</span>
                  <span className="text-sm font-medium text-blue-600">
                    ${deposit?.depositBalance?.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                Date:{" "}
                {deposit?.paymentDate
                  ? format(new Date(deposit.paymentDate), "MMM dd, yyyy")
                  : "N/A"}
              </div>
              {deposit?.depositUsage && deposit.depositUsage.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-900">Usage History</h3>
                  <div className="bg-gray-50/50 rounded-sm border border-gray-100 p-3 space-y-2">
                    {deposit.depositUsage.map((usage, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-gray-700">
                            Invoice: {usage?.invoice?.invoiceId || "N/A"}
                          </span>
                        </div>
                        <div className="flex gap-4">
                          <span className="text-gray-700">
                            Amount Used: $
                            {usage?.amountUsed?.toFixed(2) || "0.00"}
                          </span>
                          <span className="text-gray-500">
                            {usage?.dateUsed
                              ? format(new Date(usage.dateUsed), "MMM dd, yyyy")
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Collect Deposit Modal */}
      {showCollectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity"
            onClick={() => setShowCollectModal(false)}></div>
          <div className="bg-white rounded-sm shadow-lg border border-gray-200 max-w-lg w-full relative z-10">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Collect Deposit</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Payment Method
                </label>
                <select
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentMethod}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentMethod: e.target.value,
                    })
                  }>
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {method.charAt(0).toUpperCase() + method.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Amount
                </label>
                <input
                  type="number"
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentAmount}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentAmount: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Payment Date
                </label>
                <input
                  type="date"
                  className="flex h-9 w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={paymentDetails.paymentDate}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      paymentDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCollectDeposit}
                  className="inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-9 px-4 py-2 shadow-sm">
                  Collect
                </button>
                <button
                  type="button"
                  onClick={() => setShowCollectModal(false)}
                  className="inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-100 text-gray-900 hover:bg-gray-200 h-9 px-4 py-2 shadow-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
